import React, { useMemo } from 'react';
import { Paper, Typography } from '@mui/material';
import type { ConditionLifecycleSummary } from '../../domain/continuousSafety/siteConditionState';
import { isApiLifecycleHref } from '../../domain/continuousSafety/conditionLifecycleNavigation';
import { buildVisualLifecycleModel } from '../../domain/continuousSafety/visualLifecycleModel';
import { ConditionLifecycleSpine } from './ConditionLifecycleSpine';
import { VisualLifecycleExperience } from './VisualLifecycleExperience';

export function ScanConditionLifecycleStory({
  lifecycle,
  siteId,
  conditionId,
  zoneId,
  conditionFamily,
  safetyCaseId,
  reportId,
  timeZone,
  onNavigate,
}: {
  lifecycle: ConditionLifecycleSummary;
  siteId: string;
  conditionId: string;
  zoneId?: string | null;
  conditionFamily?: string | null;
  safetyCaseId?: string | null;
  reportId?: string | null;
  timeZone?: string | null;
  onNavigate: (path: string) => void;
}) {
  const scan = lifecycle.latestScan;
  const hasMotivatingIncident = Boolean(
    scan?.motivatingIncidents.length
    || lifecycle.latestTemporalPatternIncident
    || lifecycle.latestImmediateIncident,
  );
  const patternMotivated = (
    scan?.motivatingIncidents[0]?.triggerKind === 'TEMPORAL_PATTERN'
    || (!scan?.motivatingIncidents.length && lifecycle.latestTemporalPatternIncident)
  );
  const controlText = scan
    ? (
      scan.controlRevisionId
        ? `Scan linked to Control; Since ${lifecycle.sinceStartedAt || 'implementation time unavailable'}`
        : 'Scan not linked to a Control'
    )
    : (
      lifecycle.controlRevisionId
        ? `Control recorded; Since ${lifecycle.sinceStartedAt || 'implementation time unavailable'}`
        : 'No Control linked'
    );
  const visualModel = useMemo(() => buildVisualLifecycleModel({
    lifecycle,
    route: {
      siteId,
      conditionId,
      zoneId,
      conditionFamily,
      safetyCaseId,
    },
  }), [lifecycle, siteId, conditionId, zoneId, conditionFamily, safetyCaseId]);
  const go = (path: string) => {
    if (isApiLifecycleHref(path)) return;
    onNavigate(path);
  };
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderRadius: 3 }}
      data-testid="scan-condition-lifecycle"
    >
      <Typography variant="overline" color="text.secondary">
        Why this Scan was run
      </Typography>
      <Typography variant="body2" data-testid="scan-lifecycle-chain">
        Alarm → Control → Since → Scan
      </Typography>
      <Typography variant="body2">
        {hasMotivatingIncident
          ? `Motivating ${patternMotivated ? 'pattern' : 'immediate'} alarm recorded`
          : 'No motivating alarm was recorded'}
        {' · '}{controlText}
      </Typography>
      <ConditionLifecycleSpine
        lifecycle={lifecycle}
        siteId={siteId}
        conditionId={conditionId}
        zoneId={zoneId}
        conditionFamily={conditionFamily}
        safetyCaseId={safetyCaseId}
        timeZone={timeZone}
        density="scan"
        onNavigate={go}
        testId="scan-condition-lifecycle-spine"
      />
      <VisualLifecycleExperience
        model={visualModel}
        timeZone={timeZone}
        density="scan"
        onNavigate={go}
      />
      {scan ? (
        <Typography variant="body2" color="text.secondary">
          Before report {scan.beforeReportId || 'not linked'}
          {' · '}After report {scan.afterReportId || reportId || 'not linked'}
          {' · '}{scan.resultDirection || 'comparison pending'}
          {' · Coverage '}{scan.coverageState || 'unavailable'}
        </Typography>
      ) : null}
    </Paper>
  );
}
